// Predictive Search Functionality
class PredictiveSearch {
  constructor() {
    this.cache = new Map();
    this.searchDelay = 300;
    this.minQueryLength = 2;
    this.init();
  }

  init() {
    this.searchInput = document.querySelector('.predictive-search__input');
    this.resultsContainer = document.querySelector('.predictive-search__results');
    
    if (!this.searchInput || !this.resultsContainer) return;

    this.searchInput.addEventListener('input', this.debounce(this.handleInput.bind(this), this.searchDelay));
    this.searchInput.addEventListener('focus', this.handleFocus.bind(this));
    this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  async handleInput(event) {
    const query = event.target.value.trim();
    
    if (query.length < this.minQueryLength) {
      this.hideResults();
      return;
    }

    if (this.cache.has(query)) {
      this.displayResults(this.cache.get(query));
      return;
    }

    this.showLoading();
    
    try {
      const results = await this.search(query);
      this.cache.set(query, results);
      this.displayResults(results);
    } catch (error) {
      console.error('Search error:', error);
      this.showError();
    }
  }

  async search(query) {
    const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`);
    
    if (!response.ok) {
      throw new Error('Search request failed');
    }
    
    const data = await response.json();
    return data.resources.results.products || [];
  }

  displayResults(products) {
    if (products.length === 0) {
      this.showNoResults();
      return;
    }

    const resultsHtml = products.map(product => this.createProductItem(product)).join('');
    this.resultsContainer.innerHTML = resultsHtml;
    this.showResults();
  }

  createProductItem(product) {
    const price = product.price ? window.formatMoney(product.price, window.moneyFormats.predictiveSearch) : '';
    const image = product.featured_image ? product.featured_image.url : '';
    
    return `
      <a href="${product.url}" class="predictive-search__item">
        ${image ? `<img src="${image}" alt="${product.title}" class="predictive-search__image" loading="lazy">` : ''}
        <div class="predictive-search__content">
          <h4 class="predictive-search__title">${product.title}</h4>
          ${price ? `<div class="predictive-search__price">${price}</div>` : ''}
        </div>
      </a>
    `;
  }

  showLoading() {
    this.resultsContainer.innerHTML = '<div class="predictive-search__loading">Searching...</div>';
    this.showResults();
  }

  showNoResults() {
    this.resultsContainer.innerHTML = '<div class="predictive-search__no-results">No products found</div>';
    this.showResults();
  }

  showError() {
    this.resultsContainer.innerHTML = '<div class="predictive-search__no-results">Search temporarily unavailable</div>';
    this.showResults();
  }

  showResults() {
    this.resultsContainer.classList.add('predictive-search__results--visible');
  }

  hideResults() {
    this.resultsContainer.classList.remove('predictive-search__results--visible');
  }

  handleFocus() {
    if (this.searchInput.value.length >= this.minQueryLength && this.resultsContainer.children.length > 0) {
      this.showResults();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.hideResults();
      this.searchInput.blur();
    }
  }

  handleDocumentClick(event) {
    if (!event.target.closest('.predictive-search')) {
      this.hideResults();
    }
  }
}

// Initialize predictive search when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearch();
});

// Format money utility
window.formatMoney = window.formatMoney || function(cents, format) {
  if (typeof cents === 'string') { cents = cents.replace('.',''); }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || window.moneyFormats?.default || '${{amount}}');
  
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision || 2;
    thousands = thousands || ',';
    decimal = decimal || '.';
    
    if (isNaN(number) || number == null) { return 0; }
    
    number = (number/100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? (decimal + parts[1]) : '';
    
    return dollars + cents;
  }
  
  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }
  
  return formatString.replace(placeholderRegex, value);
};

// Set default money formats
window.moneyFormats = window.moneyFormats || {
  default: '${{amount}}',
  predictiveSearch: '${{amount}}'
};